import type { ActionValueTypeValueOf } from "replo-runtime/shared/enums";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import { selectDraftElementHashmarks } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { CSS_LENGTH_TYPES } from "replo-runtime/shared/utils/units";

type OffsetHashmarkProps = {
  value: ActionValueTypeValueOf<"offsetHashmark"> | null | undefined;
  onChange: (value: ActionValueTypeValueOf<"offsetHashmark"> | null) => void;
};

const OffsetHashmarkSelector: React.FC<OffsetHashmarkProps> = ({
  value,
  onChange,
}) => {
  const normalizedValue =
    typeof value === "string"
      ? { hashmark: value, offset: 0, smoothScroll: true }
      : value;
  const elementHashmarks = useEditorSelector(selectDraftElementHashmarks);
  const isCustomHashmark =
    (normalizedValue?.hashmark != null &&
      !elementHashmarks.includes(normalizedValue.hashmark)) ||
    elementHashmarks.length === 0;

  const [isCustomSelected, setIsCustomSelected] = React.useState<
    boolean | null
  >(isCustomHashmark);

  const selectableOptions = [
    ...elementHashmarks.map((hashmark) => ({
      label: hashmark,
      value: hashmark,
    })),
    { label: "Custom Hashmark", value: "custom-replo-hashmark" },
  ];

  const onSelectableChange = (selectableValue: string) => {
    const commonProps = {
      offset: normalizedValue?.offset ?? 0,
      smoothScroll: normalizedValue?.smoothScroll ?? true,
    };
    if (selectableValue === "custom-replo-hashmark") {
      setIsCustomSelected(true);
    } else {
      setIsCustomSelected(false);
      onChange({
        hashmark: selectableValue,
        ...commonProps,
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Selectable
          placeholder="Select a hashmark"
          options={selectableOptions}
          value={
            isCustomSelected
              ? "custom-replo-hashmark"
              : normalizedValue?.hashmark
          }
          defaultValue={
            elementHashmarks.length === 0 ? "custom-replo-hashmark" : undefined
          }
          onSelect={onSelectableChange}
        />
        {isCustomSelected && (
          <DeprecatedInput
            autoFocus
            size="sm"
            value={normalizedValue?.hashmark}
            placeholder="your-hashmark-here"
            onChange={(e) => {
              onChange({
                ...normalizedValue,
                offset: normalizedValue?.offset ?? 0,
                hashmark: e.target?.value?.replace("#", ""),
                smoothScroll: normalizedValue?.smoothScroll ?? true,
              });
            }}
          />
        )}
      </div>
      <DeprecatedLabeledControl label="Offset" size="sm">
        <LengthInputSelector
          className="w-full"
          value={normalizedValue?.offset?.toString() ?? null}
          onChange={(stringValue: string) => {
            onChange({
              ...normalizedValue,
              offset: stringValue ? Number.parseInt(stringValue) : undefined,
            });
          }}
          draggingType="vertical"
          field="HashmarkOffset"
          placeholder="0px"
          metrics={CSS_LENGTH_TYPES}
          minValues={{ px: 0 }}
          allowsNegativeValue={false}
        />
      </DeprecatedLabeledControl>
      <SwitchWithDescription
        isOn={normalizedValue?.smoothScroll ?? true}
        onChange={(isOn) => {
          onChange({
            ...normalizedValue,
            smoothScroll: isOn,
          });
        }}
        size="sm"
        label="Smooth Scroll"
      />
    </div>
  );
};

export default OffsetHashmarkSelector;
